import { userService } from "@/app/api/user";
import { useUserContext } from "@/context/user.context";
import {
  ActionIcon,
  Button,
  Flex,
  Text,
  TextInput,
  PasswordInput,
} from "@mantine/core";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useCallback, useMemo, useState } from "react";

const isValidPassword = (password: string): boolean => {
  const invalidPattern = /^(.{0,7}|[^0-9]*|[^A-Z]*|[^a-z]*|[a-zA-Z0-9]*)$/;
  return !invalidPattern.test(password);
};

export default function ChangePassword({ onClose }: { onClose: () => void }) {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordBlurred, setNewPasswordBlurred] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user, refreshUser } = useUserContext();
  const router = useRouter();

  const handleSave = useCallback(() => {
    if (user) {
      setIsLoading(true);
      setError(null);
      userService
        .updatePassword(oldPassword, newPassword)
        .then(() => {
          refreshUser();
          onClose();
        })
        .catch((err) => {
          const message =
            err?.response?.data?.message || "Failed to update password";
          setError(message);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [oldPassword, newPassword, onClose, refreshUser, user]);

  const isPasswordValid = useMemo(
    () => isValidPassword(newPassword),
    [newPassword],
  );

  const passwordError = useMemo(() => {
    if (!newPasswordBlurred) {
      return null;
    }

    return isPasswordValid
      ? null
      : "Password must be at least 8 characters and include uppercase, lowercase, number, and special character.";
  }, [isPasswordValid, newPasswordBlurred]);

  const isValid = useMemo(() => {
    return Boolean(oldPassword && newPassword && isPasswordValid);
  }, [oldPassword, newPassword, isPasswordValid]);

  return (
    <Flex align="flex-start" direction="column" gap="sm">
      <Flex align="center" className="w-full" justify="space-between">
        <Text fw={600}>Change Password</Text>
        <ActionIcon onClick={onClose} variant="transparent">
          <Image
            src="/assets/circle-x.svg"
            alt="Close Account Modal"
            height={16}
            width={16}
          />
        </ActionIcon>
      </Flex>
      <Flex direction="column" gap={0} align="flex-start">
        <TextInput
          className="w-[300px]"
          label="Old Password"
          type="password"
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
        />
        <Button
          variant="transparent"
          className="text-uw-purple-6 uppercase"
          size="xs"
          onClick={() => {
            router.push("/auth/forgot");
          }}
          p={0}
        >
          I don’t have my old password
        </Button>
      </Flex>
      <PasswordInput
        className="w-[300px]"
        label="New Password"
        value={newPassword}
        error={error || passwordError}
        onChange={(e) => {
          if (error) {
            setError(null);
          }
          setNewPassword(e.target.value);
        }}
        onBlur={() => {
          if (newPassword) {
            setNewPasswordBlurred(true);
          }
        }}
      />
      <Flex mt={21}>
        <Button
          className="disabled:bg-uw-green disabled:opacity-50 disabled:text-white"
          color="mint-green.8"
          disabled={!isValid}
          mr={9}
          onClick={handleSave}
          loading={isLoading}
        >
          Update Password
        </Button>
        <Button className="text-uw-gray-9" color="gray.0" onClick={onClose}>
          Cancel
        </Button>
      </Flex>
    </Flex>
  );
}
